import React from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { motion, AnimatePresence } from 'framer-motion';
import {
  LayoutDashboard, Building2, Sparkles, Users, ClipboardCheck, Receipt,
  FileStack, MapPinned, ShoppingBag, ScrollText, LogOut, ListChecks, TrendingUp, Wallet, UserCog, Settings2, Gavel, User, BarChart3, ShieldCheck, Globe2, HelpCircle, Sunrise, Trophy, KeyRound, Database, Presentation
} from 'lucide-react';
import AIChat from './AIChat';
import NotificationBell from './NotificationBell';
import MobileBottomNav from './MobileBottomNav';
import PWAInstallPrompt from './PWAInstallPrompt';
import PlatformTour from './PlatformTour';

const NAV_GROUPS = [
  {
    key: 'overview',
    items: [
      { to: '/app/dashboard', label: 'Dashboard', icon: LayoutDashboard },
      { to: '/app/me',        label: 'My Workspace', icon: User },
    ],
  },
  {
    key: 'portfolio',
    label: 'Portfolio',
    items: [
      { to: '/app/properties', label: 'Assets',     icon: Building2 },
      { to: '/app/sales',      label: 'Sales',      icon: ShoppingBag },
    ],
  },
  {
    key: 'market',
    label: 'Market Intelligence',
    items: [
      { to: '/app/daily-digest',  label: 'Morning Brief', icon: Sunrise },
      { to: '/app/market',        label: 'Trends',        icon: BarChart3 },
      { to: '/app/market/live',   label: 'Live Listings', icon: Globe2 },
      { to: '/app/market/transactions', label: 'Transactions',  icon: Database },
      { to: '/app/market/leaderboard', label: 'Rental Leaderboard', icon: Trophy },
      { to: '/app/auctions',      label: 'Auctions',      icon: Gavel },
      { to: '/app/deal-analyzer', label: 'Deal Analyzer', icon: Sparkles },
    ],
  },
  {
    key: 'ops',
    label: 'Operations',
    items: [
      { to: '/app/vendors',   label: 'Vendors',            icon: Users },
      { to: '/app/tasks',     label: 'Tasks',              icon: ListChecks },
      { to: '/app/drawings',  label: 'Drawings & Permits', icon: MapPinned },
      { to: '/app/documents', label: 'Documents',          icon: FileStack },
    ],
  },
  {
    key: 'finance',
    label: 'Finance',
    items: [
      { to: '/app/accounting', label: 'Accounting',       icon: Receipt },
      { to: '/app/approvals',  label: 'Approvals',        icon: ClipboardCheck },
      { to: '/app/cash-flow',  label: 'Cash Flow',        icon: TrendingUp },
      { to: '/app/retention',  label: 'Retention',        icon: Wallet },
    ],
  },
  {
    key: 'workspace',
    label: 'Workspace',
    items: [
      { to: '/app/team',  label: 'Team',           icon: UserCog },
      { to: '/app/admin', label: 'Admin Settings', icon: Settings2, adminOnly: true },
      { to: '/app/audit', label: 'Audit Log',      icon: ScrollText },
    ],
  },
  {
    key: 'support',
    label: 'Support',
    items: [
      { to: '/app/present', label: 'Present the Platform', icon: Presentation },
      { to: '/app/help', label: 'Help Center', icon: HelpCircle },
    ],
  },
];

export default function Layout({ children }) {
  const { user, workspace, switchWorkspace, logout } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const [chatOpen, setChatOpen] = React.useState(false);

  return (
    <div className="min-h-screen flex">
      <div className="ambient-bg" />
      {/* Sidebar */}
      <aside className="w-64 hidden md:flex flex-col p-5 relative z-10" data-testid="sidebar">
        <div className="glass px-4 py-4 mb-6 flex items-center gap-3">
          <div className="w-11 h-11 rounded-2xl bg-white grid place-items-center shadow-md overflow-hidden p-1.5 border border-slate-200">
            <img src="/iris-icon.svg" alt="Iris" className="w-full h-full object-contain" />
          </div>
          <div>
            <div className="font-serif text-xl leading-none">Iris Realty</div>
            <div className="overline mt-1" style={{ fontSize: '0.6rem' }}>Property OS</div>
          </div>
        </div>

        <nav className="flex-1 flex flex-col gap-1 overflow-y-auto" data-testid="sidebar-nav">
          {NAV_GROUPS.map((grp) => {
            const visibleItems = grp.items.filter(n => !n.adminOnly || (user && ['owner','md'].includes(user.role)));
            if (visibleItems.length === 0) return null;
            return (
              <div key={grp.key} className={grp.label ? 'mt-3 first:mt-0' : ''}>
                {grp.label && (
                  <div
                    className="px-4 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400/90"
                    data-testid={`nav-group-${grp.key}`}
                  >
                    {grp.label}
                  </div>
                )}
                {visibleItems.map(({ to, label, icon: Icon }) => (
                  <NavLink
                    key={to}
                    to={to}
                    end
                    data-testid={`nav-${label.toLowerCase().replace(/[^a-z]+/g, '-')}`}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-4 py-2 rounded-full text-sm transition-all ${
                        isActive
                          ? 'bg-[#1A2530] text-white shadow-lg shadow-[#1A2530]/20'
                          : 'text-slate-700 hover:bg-white/60'
                      }`
                    }
                  >
                    <Icon size={16} />
                    <span>{label}</span>
                  </NavLink>
                ))}
              </div>
            );
          })}
        </nav>

        <div className="glass mt-4 p-4">
          <div className="flex items-center gap-3">
            <img src={user?.picture || `https://ui-avatars.com/api/?name=${encodeURIComponent(user?.name || 'U')}&background=1A2530&color=fff`}
                 alt="" className="w-10 h-10 rounded-full border-2 border-white" />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium truncate">{user?.name}</div>
              <div className="overline" style={{ fontSize: '0.55rem' }}>{user?.role?.replace('_', ' ')}</div>
            </div>
            <button onClick={logout} className="text-slate-500 hover:text-red-500" data-testid="logout-btn" title="Logout">
              <LogOut size={16} />
            </button>
          </div>
          <a href="/app/security" data-testid="security-link"
            className="mt-2 flex items-center gap-2 text-[11px] text-slate-500 hover:text-slate-800">
            <ShieldCheck size={11}/> Security &amp; 2FA
          </a>
          <a href="/app/change-password" data-testid="change-password-link"
            className="mt-1 flex items-center gap-2 text-[11px] text-slate-500 hover:text-slate-800">
            <KeyRound size={11}/> Change password
          </a>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top bar — hidden on mobile-snag which has its own header */}
        {!location.pathname.startsWith('/app/mobile-snag') && (
        <header className="px-5 md:px-8 py-4">
          <div className="glass px-4 py-2.5 flex items-center gap-4">
            <div className="md:hidden font-serif text-lg">Iris Realty</div>
            <div className="flex-1" />
            {/* Live / Demo toggle */}
            <div className="flex items-center gap-2 bg-white/70 rounded-full p-1 border border-white/80 shadow-inner" data-testid="workspace-toggle">
              <button
                data-testid="workspace-live"
                onClick={() => switchWorkspace('live')}
                className={`px-4 py-1.5 rounded-full text-xs font-medium transition-all flex items-center gap-2 ${
                  workspace === 'live' ? 'bg-[#1A2530] text-white shadow-md' : 'text-slate-600'
                }`}
              >
                <span className={`w-2 h-2 rounded-full ${workspace === 'live' ? 'bg-emerald-400 shadow-[0_0_8px_rgba(16,185,129,0.8)]' : 'bg-slate-400'}`} />
                Live
              </button>
              <button
                data-testid="workspace-demo"
                onClick={() => switchWorkspace('demo')}
                className={`px-4 py-1.5 rounded-full text-xs font-medium transition-all flex items-center gap-2 ${
                  workspace === 'demo' ? 'bg-[#91B7AC] text-white shadow-md' : 'text-slate-600'
                }`}
              >
                Demo
              </button>
            </div>
            <button
              data-testid="open-ai-chat"
              onClick={() => setChatOpen(true)}
              className="btn-gold text-sm flex items-center gap-2 !p-0 w-10 h-10 md:!p-4 md:w-auto md:h-auto justify-center md:justify-start rounded-full"
              aria-label="Ask Iris AI"
              title="Ask Iris AI"
            >
              <Sparkles size={16} />
              <span className="hidden md:inline">Ask Iris AI</span>
            </button>
            <NotificationBell />
          </div>
        </header>
        )}

        <main className="flex-1 px-5 md:px-8 pb-16">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
          >
            {children}
          </motion.div>
        </main>
      </div>

      <AnimatePresence>
        {chatOpen && <AIChat onClose={() => setChatOpen(false)} />}
      </AnimatePresence>

      <MobileBottomNav />
      <PWAInstallPrompt />
      <PlatformTour />
    </div>
  );
}
